// The banner's contrast (01, Contrast targets, v1.58.48): on a picture the members line reaches 4.5:1 and
// the name 3:1 against what is actually behind them, the "⋯" 3:1. The usual scrim (black 45 % fading out
// at 60 % of the height; in the side panel 55 % and 75 %, v1.58.62) is the starting point; where the picture is too light behind the text band the
// scrim under it deepens to a flat black, up to 70 %, until both are met. A light top right gives the "⋯"
// a dark backing.

const lin = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const ratio = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
/** A grey (0–1, sRGB) under black at `alpha`, and white at `ink` over that: their contrast. */
const over = (grey: number, alpha: number, ink: number) => {
  const under = grey * (1 - alpha);
  return ratio(lin(ink + (1 - ink) * under), lin(under));
};

/**
 * Where the members line (white 90 %) and the name (white) stand, from the bottom; the text's sides and
 * the "⋯"'s place, from the left, right and top; the usual scrim's black at the bottom and where it has
 * faded out (a share of the height). The 136 px banner's (the phone's, the previews'):
 */
export const USUAL = { members: [14, 30], name: [30, 58], left: 14, right: 54, menu: [10, 54], black: 0.45, fade: 0.6 } as const;
/** The side panel's 96 px banner (07, v1.58.62): 12 px in, the name 20 px ending 8 px before the "⋯" 8 px in; black 55 % to 75 %. */
export const PANEL = { members: [12, 28], name: [30, 56], left: 12, right: 46, menu: [8, 38], black: 0.55, fade: 0.75 } as const;
type Layout = typeof USUAL | typeof PANEL;

/**
 * The scrim for a picture whose lightest greys (sRGB, 0–1) behind the members line and the name are
 * `members` and `name`, in a banner `height` px tall: undefined when the usual one meets the targets,
 * else the flat black's alpha (at most 0.7).
 */
export function scrimFor(members: number, name: number, height: number, at: Layout = USUAL): number | undefined {
  const usual = (y: number) => at.black * Math.max(0, 1 - y / (at.fade * height));
  if (over(members, usual(at.members[1]), 0.9) >= 4.5 && over(name, usual(at.name[1]), 1) >= 3) return undefined;
  for (let alpha = at.black; alpha < 0.7; alpha += 0.01) if (over(members, alpha, 0.9) >= 4.5 && over(name, alpha, 1) >= 3) return Math.round(alpha * 100) / 100;
  return 0.7;
}

/** Whether white on the "⋯"'s light backing (white 18 %) falls under 3:1 over this grey. */
export const lightTop = (grey: number) => ratio(1, lin(0.18 + 0.82 * grey)) < 3;

/** The lightest grey (the 95th percentile of luminance, as sRGB) of a region of RGBA pixels. */
function lightest(data: Uint8ClampedArray, width: number, x0: number, y0: number, x1: number, y1: number) {
  const seen: number[] = [];
  for (let y = Math.max(0, y0); y < y1; y++)
    for (let x = Math.max(0, x0); x < Math.min(width, x1); x++) {
      const i = (y * width + x) * 4;
      seen.push(0.2126 * lin(data[i] / 255) + 0.7152 * lin(data[i + 1] / 255) + 0.0722 * lin(data[i + 2] / 255));
    }
  if (!seen.length) return 0;
  seen.sort((a, b) => a - b);
  const l = seen[Math.floor(seen.length * 0.95)];
  return l <= 0.0031308 ? l * 12.92 : 1.055 * l ** (1 / 2.4) - 0.055;
}

/** Measures the picture at `src` as the banner draws it (cover, `width` × `height`); undefined when it can't be read. */
export async function measure(src: string, width: number, height: number, at: Layout = USUAL): Promise<{ scrim?: number; light: boolean } | undefined> {
  try {
    const picture = await createImageBitmap(await (await fetch(src)).blob());
    const canvas = new OffscreenCanvas(Math.round(width), Math.round(height));
    const g = canvas.getContext('2d', { willReadFrequently: true })!;
    const k = Math.max(width / picture.width, height / picture.height);
    g.drawImage(picture, (width - picture.width * k) / 2, (height - picture.height * k) / 2, picture.width * k, picture.height * k);
    const { data } = g.getImageData(0, 0, canvas.width, canvas.height);
    const [w, h] = [canvas.width, canvas.height];
    const band = ([from, to]: readonly [number, number]) => lightest(data, w, at.left, h - to, w - at.right, h - from);
    return {
      scrim: scrimFor(band(at.members), band(at.name), height, at),
      light: lightTop(lightest(data, w, w - at.menu[1], at.menu[0], w - at.menu[0], at.menu[1])),
    };
  } catch {
    return undefined; // the usual scrim
  }
}
